import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { facilitatorAPI } from '../lib/api';
import { FacilitatorCard } from '../components/ListingCard';
import { SearchFilters } from '../components/SearchFilters';
import { Skeleton } from '../components/ui/skeleton';
import { Users, AlertCircle } from 'lucide-react';

const FacilitatorsDirectory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [facilitators, setFacilitators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFacilitators();
  }, [searchParams]);

  const loadFacilitators = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = Object.fromEntries(searchParams.entries());
      const response = await facilitatorAPI.list(params);
      setFacilitators(response.data);
    } catch (err) {
      setError('Failed to load facilitators. Please try again.');
      console.error('Error loading facilitators:', err);
    }
    setLoading(false);
  };

  const handleFilterChange = (filters) => {
    const newParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) newParams.set(key, value);
    });
    setSearchParams(newParams);
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      {/* Header */}
      <div className="bg-muted/30 border-b border-border">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground">
                Find Facilitators
              </h1>
              <p className="text-sm text-muted-foreground">
                {facilitators.length} facilitators available
              </p>
            </div>
          </div>

          <SearchFilters
            type="facilitator"
            onFilterChange={handleFilterChange}
            initialFilters={Object.fromEntries(searchParams.entries())}
          />
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden">
                <Skeleton className="aspect-[4/3] w-full" />
                <div className="p-5 space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <p className="text-destructive font-medium">{error}</p>
          </div>
        ) : facilitators.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
              No facilitators found
            </h3>
            <p className="text-muted-foreground">
              Try adjusting your filters or search terms.
            </p>
          </div>
        ) : (
          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            data-testid="facilitators-grid"
          >
            {facilitators.map((facilitator) => (
              <FacilitatorCard key={facilitator.id} facilitator={facilitator} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FacilitatorsDirectory;
